import { useState, useEffect, useRef } from 'react'

interface UseDebouncedSearchOptions {
  minLength?: number
  delay?: number
}

/**
 * Custom hook for debounced search with minimum character requirement
 * 
 * @param value - The search input value
 * @param options - Configuration options
 * @param options.minLength - Minimum characters required before triggering search (default: 3)
 * @param options.delay - Debounce delay in milliseconds (default: 500)
 * 
 * @returns The debounced search value (empty string if below minLength)
 * 
 * @example
 * const [searchQuery, setSearchQuery] = useState('')
 * const debouncedSearch = useDebouncedSearch(searchQuery, { minLength: 3, delay: 500 })
 * 
 * useEffect(() => {
 *   if (debouncedSearch) {
 *     // Make API call with debouncedSearch
 *   }
 * }, [debouncedSearch])
 */
const useDebouncedSearch = (
  value: string,
  options: UseDebouncedSearchOptions = {}
): string => {
  const { minLength = 3, delay = 500 } = options
  const [debouncedValue, setDebouncedValue] = useState<string>('')
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const trimmedValue = value.trim()

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // If value is empty, clear debounced value immediately (allow API call with empty search)
    if (trimmedValue.length === 0) {
      setDebouncedValue('')
      return
    }

    // If value is below minimum length, clear debounced value (show all results)
    // This ensures API is not called until minimum length is reached
    if (trimmedValue.length < minLength) {
      setDebouncedValue('')
      return
    }

    // Debounce the value update for values meeting minimum length requirement
    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(trimmedValue)
    }, delay)

    // Cleanup function to cancel pending debounce on unmount or value change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [value, minLength, delay])

  return debouncedValue
}

export default useDebouncedSearch
